/*  Table  */
import { IconName, IconVariantType } from '@auviknetworks/iceberg-ui';
import {
    ColumnFiltersState,
    ColumnDef as ReactTableColumnDef,
    Row,
    SortingFn,
    SortingFnOption,
    Table
} from '@tanstack/react-table';
import { DataAPI } from 'application/Shared/Requests';
import { Dispatch, SetStateAction } from 'react';

/**
 * Base table context interface for ManagedTableV2
 * @interface BaseTableContext
 * @template T - The type of data in the rows
 */
export interface BaseTableContext<T> {
    apiSize?: number;
    autoupdate?: boolean;
    baseParameters?: object;
    bulkActions?: React.ReactElement;
    colSpan: number;
    data?: T[]; // Filtered data to be displayed in the table
    dataAPI?: DataAPI;
    defaultSearch?: string;
    externalLoading?: boolean;
    filters?: object;
    getRowId?: string;
    handleRows?: (rows: T[]) => T[];
    handleSwitchChange: (id: string, value: boolean) => void;
    ignoreSearchParams?: boolean;
    initialState?: object;
    manualFiltering?: boolean;
    preprocessRows?: (rows: T[]) => T[];
    rerender: boolean;
    resetRowSelection?: boolean;
    rowCount?: number;
    rowData: T[]; // Data from API response to be displayed in the table
    rows?: T[];
    rowsApiPreload?: boolean;
    rowsPerPage?: number;
    searchEnabled?: boolean;
    searchText: string;
    setFilters?: (filters: object) => void;
    setParameters?: (params: object) => void;
    setRerender?: Dispatch<SetStateAction<boolean>>;
    setResetRowSelection?: Dispatch<SetStateAction<boolean>>;
    setSearchText?: Dispatch<SetStateAction<string>>;
    setToggles?: (toggles: ToggleState) => void;
    showAutoupdate?: boolean;
    sortDirection?: 'asc' | 'desc';
    sortKey?: string;
    t?: (key: string) => string;
    table: Table<T>;
    tableIncludesFilters?: boolean;
    tableName: string;
    tableViews: View[] | undefined;
    toggles: ToggleState;
}

export interface ApiCustomTableContext<T>
    extends ApiTableContext<T>,
        CustomTableContext<T> {}

/**
 * API table context interface for ManagedTableV2
 * @interface APITableContext
 * @template T - The type of data in the rows
 */
export interface ApiTableContext<T> extends BaseTableContext<T> {
    apiLoading: boolean;
    apiTable: boolean;
    parameters: object;
    rowsApi: string;
    startPolling: boolean | undefined;
}

/**
 * Custom table context interface for ManagedTableV2
 * @interface CustomTableContext
 * @template T - The type of data in the rows
 */
export interface CustomTableContext<T> extends BaseTableContext<T> {
    currentTableView:
        | View
        | CustomTableView
        | Record<string, never>
        | undefined;
    customTableViews: CustomTableView[] | undefined;
    defaultView: View;
    refetchCustomTableViews: () => void;
    setCurrentTableView: (
        view: View | CustomTableView | Record<string, never>
    ) => void;
    setShowFlyoutMenu: (action: ShowFlyoutMenuActionEnum) => void;
    setViewHasBeenModified: Dispatch<SetStateAction<boolean>>;
    showFlyoutMenu: ShowFlyoutMenuActionEnum;
    viewHasBeenModified?: boolean;
}

export interface CSVTableContext<T> extends BaseTableContext<T> {
    dataAPI: DataAPI;
    rowCount: number;
}

export interface Toggle {
    defaultEnabled: boolean;
    forceParameter?: boolean;
    key: string;
    parameter: { field: string; value: boolean };
    toggleText: string;
    tooltip?: string;
}

export interface ToggleState {
    [key: string]: Toggle & { enabled: boolean };
}

export enum ShowFlyoutMenuActionEnum {
    edit = 'edit',
    save = 'saveAsNewView',
    delete = 'delete',
    hide = 'hide'
}

export enum AccessType {
    personal = 'personal',
    restricted = 'restricted',
    tenant = 'tenant'
}

export interface CustomTableView {
    accessType: AccessType;
    config: TableViewState;
    dateCreated: string;
    dateUpdated: string;
    description: string;
    displayName: string;
    id: string;
    platformTenantId: string;
    platformUserId: string;
    state?: TableViewState;
    targetTable: string;
}

/**
 * Interface for the return value of the userCustomTableView delete function
 */
export interface DeleteUserCustomTableViewResult {
    data: boolean;
    message: string;
    serverResponse: number;
}

/**
 * Interface for the return value of the userCustomTableView fetch/get function
 */
export interface GetUserCustomTableViewResult {
    customTableViews: Array<CustomTableView>;
    loading: boolean;
    refetchCustomTableViews: () => void;
}

export interface TableViewState {
    columnFilters?: ColumnFiltersState;
    columnOrder?: string[];
    columnVisibility?: object;
    sorting?: object[];
}

/**
 * Interface for the params of the userCustomTableView POST/PUT function
 */
export interface PostPutUserCustomTableViewParams {
    accessType: AccessType;
    config: TableViewState;
    description: string;
    displayName: string;
    id?: string;
    targetTable: string;
}

export interface View {
    accessType: AccessType;
    displayName: string;
    id: string;
    state: TableViewState;
}

export interface FilterOptions {
    group?: string;
    options: string[];
}

export interface ColumnFilter<T> {
    defaultOptions: FilterOptions[];
    clientSideFiltering?: boolean;
    control?: 'Checkbox' | 'Radio';
    filterFn?: (row: T, filterValues: string | string[]) => boolean;
    includeNullValue?: boolean;
}

export interface CustomColumnMeta<T> {
    align?: 'left' | 'right' | 'center';
    header?: {
        justifyContent: string;
    };
    icon?: {
        size?: number;
        src: IconName;
        variant?: IconVariantType;
    };
    filter?: ColumnFilter<T>;
}

export interface ColumnBuilder<TData> {
    cellType: CellType;
    enableColumnFilter: boolean;
    filterFn?: (
        row: Row<TData>,
        columnId: string,
        filterValue: string[]
    ) => boolean;
    headerIcon?: {
        size?: number;
        src: IconName;
        tooltipText: string;
        variant?: IconVariantType;
    };
    id: string;
    meta?: CustomColumnMeta<TData>;
    name: string;
    property: string;
    positionFromEnd?: number;
    props?: {
        [key: string]: unknown;
    };
    size: number;
    sortingFn?: SortingFn<TData> | SortingFnOption<TData>;
    testId: string;
}

export enum CellType {
    DateFormat = 'DateFormat',
    Icon = 'Icon',
    String = 'String'
}

/**
 * Base interface for CSV columns
 * @interface CSVColumnBase
 * @property {string} id - The id that maps to the column in the table. Used to determine if a column is selected for export.
 * @property {string} key - The key of the column where processed data will be stored
 * @property {string} label - The label of the column in the CSV file
 */
export interface CSVColumnBase {
    id?: string;
    key: string;
    label: string;
}

/**
 * Interface for CSV columns that use the default transform
 * @interface CSVColumnDefault
 * @property {string} transform - The transform to apply to the column
 * hms - Converts a number of seconds into hours, minutes, and seconds
 * bool_YN - Converts a boolean value to 'Y' or 'N'
 */
export interface CSVColumnDefault extends CSVColumnBase {
    transform?: 'hms' | 'bool_YN';
}

/**
 * Interface for CSV columns that use the listExtract transform
 * @interface CSVColumnListExtract
 * @property {string} transform - The transform to apply to the column
 * @property {string} rowKeyAndDataPath - The path to the data in the row
 * listExtract is used to extract a list of strings from an array.
 * The `key` parameter for this transform is the name of the new property to be added to the row
 * and should be different than the original key where the data is found.
 *
 * The `rowKeyAndDataPath` parameter is used to determine how to extract the data from the array.
 *
 */
export interface CSVColumnListExtract extends CSVColumnBase {
    transform: 'listExtract';
    rowKeyAndDataPath: string;
}

/**
 * Interface for CSV columns that use the objectExtract transform
 * @interface CSVColumnObjectExtract
 * @property {string} transform - The transform to apply to the column
 * @property {string} rowKeyAndDataPath - The path to the data in the row
 * @property {string} separator - The separator of the column
 * objectExtract is used to extract values from a nested object. The `key` parameter for this transform
 * is the name of the new property to be added to the row and should be different than the
 * original key where the data is found. The `rowKeyAndDataPath` parameter is used to determine
 * how to extract the data from the object. It follows the pattern of `keyToData:path.to.data`.
 *
 *
 */
export interface CSVColumnObjectExtract extends CSVColumnBase {
    transform: 'objectExtract';
    rowKeyAndDataPath: string;
    separator?: string;
}

/**
 * Interface for CSV columns that use the custom transform
 * @interface CSVColumnCustom
 * @property {string} transform - The transform to apply to the column
 * @property {string} rowKeyAndDataPath - The path to the data in the row to apply the custom function to
 * @property {function} fn - The function to apply to the data
 */
export interface CSVColumnCustom extends CSVColumnBase {
    transform: 'custom';
    rowKeyAndDataPath: string;
    fn: (value: unknown) => unknown;
}

export type CSVColumn =
    | CSVColumnDefault
    | CSVColumnListExtract
    | CSVColumnObjectExtract
    | CSVColumnCustom;

/**
 * Augmenting the Column definition with what iceberg tables V2 use
 * @param {string} [align] - aligns the column header to the left or right
 * @param {CustomColumnMeta} [meta] - sending various parameters for the column and row. E.g, filter, aligning the row data.
 * @param {string} name - name of the column
 * @param {boolean} [nowrap] - prevents the header title from wrapping
 * @param {string} [testId] - test id for the column
 * @param {string} [tooltipText] - tooltip text for the column header
 */
type AuvikColumnDef<T> = {
    align?: 'left' | 'right';
    defaultKey?: string;
    meta?: CustomColumnMeta<T>;
    name?: string;
    nowrap?: boolean;
    testId?: string;
    tooltipText?: string;
};

export type ColumnDef<T, V> = ReactTableColumnDef<T, V> & AuvikColumnDef<T>;
